import { useEffect, useState } from 'react';
import { FundType } from '../types/FundType';
import { getFundTypes } from '../api/fundTypes';

// Module-level cache shared by every hook caller for the lifetime of the page.
// `inflight` deduplicates concurrent fetches; cleared on failure so the next
// mount retries.
let cache: FundType[] | null = null;
let inflight: Promise<FundType[]> | null = null;

function loadFundTypes(): Promise<FundType[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getFundTypes()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

interface UseFundTypesResult {
  data: FundType[] | null;
  loading: boolean;
  error: string | null;
}

export function useFundTypes(): UseFundTypesResult {
  const [state, setState] = useState<UseFundTypesResult>(() =>
    cache
      ? { data: cache, loading: false, error: null }
      : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      // Cache may have populated between render and effect (e.g. a sibling
      // instance finished fetching). Adopt it; bail if state already matches.
      setState((prev) =>
        prev.data === cache
          ? prev
          : { data: cache, loading: false, error: null },
      );
      return;
    }

    let cancelled = false;
    loadFundTypes()
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
